import {
  UserManagementApi,
  TokenCheckResponse,
  TokenCheckRequest,
} from '@orca/api-user-management';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { AppLoader } from '~/components/AppLoader';
import DocumentTitle from '~/components/DocumentTitle';
import { HeaderLogo } from '~/components/HeaderLogo';
import { withSessionRedirect } from '~/components/withSessionRedirect';
import { FEATURE_FLAGS } from '~/constants/featureFlags';
import { INVITATON_TYPES } from '~/constants/invitationType';
import { PLATFORMS } from '~/constants/platforms';
import { routes } from '~/constants/routes';
import { useAlert } from '~/contexts/alert';
import { getAlertFeedBackError } from '~/contexts/alert/alertFeedBackError';
import { useSignUp } from '~/contexts/signup';
import { useFeature } from '~/hooks/useFeature';
import { SignUpScreen } from '~/screens/SignUpScreen';

function Signup() {
  const [isLoading, setIsLoading] = useState<Boolean>(true);
  const [invitationData, setInvitationData] =
    useState<TokenCheckResponse | null>();
  const router = useRouter();
  const { setAlertProps } = useAlert();
  const signUp = useSignUp();
  const { isEnabled, status } = useFeature(FEATURE_FLAGS.ssoRevamp);

  // admin invitations case
  const { invitationCode } = router.query;

  useEffect(() => {
    // Wait for feature flag status to resolve before proceeding
    if (status === 'loading') return;

    // Case 1: Feature flag enabled - redirect based on invitationCode presence
    if (isEnabled) {
      if (invitationCode) {
        // Redirect to invitation flow if invitationCode exists
        router.push(
          `${process.env.NEXT_PUBLIC_SSO_APP}/start/invitation?type=manager&code=${invitationCode}`
        );
      } else {
        // Redirect to start page if no invitationCode
        router.push(`${process.env.NEXT_PUBLIC_SSO_APP}/start?origin=foam`);
      }
      // Keep loading state true since we're redirecting
      return;
    }

    // Case 2: Feature flag disabled
    if (!invitationCode) {
      // Normal signup - show form
      setIsLoading(false);
      return;
    }

    // Case 3: Feature flag disabled with invitationCode - call tokenCheck
    UserManagementApi.tokenCheck(invitationCode as TokenCheckRequest)
      .then(resp => {
        setInvitationData(resp as TokenCheckResponse);
        signUp.setFields({
          agencyName: resp?.entity?.name,
        });
      })
      .catch(error => {
        const { status } = error?.response?.data;
        if (status === '404' || status === '422') {
          router.push(routes.foam.adminInvitationLinkError);
        } else {
          getAlertFeedBackError(error, setAlertProps);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only want to run this effect when the invitation code and feature flag status change
  }, [invitationCode, isEnabled, status]);

  return (
    <>
      <DocumentTitle>Signup</DocumentTitle>
      {isLoading ? (
        <AppLoader />
      ) : (
        <SignUpScreen
          platform={PLATFORMS.foam}
          adminInvitationEmail={invitationData?.email}
          adminInvitationCode={invitationCode as string}
          invitationType={
            invitationCode ? INVITATON_TYPES.agencyAdmin : undefined
          }
          heading={
            <HeaderLogo
              platform={PLATFORMS.foam}
              heading="Create your account"
            />
          }
        />
      )}
    </>
  );
}

export default withSessionRedirect(Signup);
